import React, { useEffect, useState } from "react";
import { api, fmtNum, fmtMoney } from "@/lib/api";
import { TrendingUp, AlertTriangle, Info } from "lucide-react";

export default function SalesMarginPreview({ form }) {
  const [data, setData] = useState(null);
  const pid = form.product_id;

  useEffect(() => {
    if (!pid) { setData(null); return; }
    const t = setTimeout(() => {
      api.get(`/landed-cost/preview?product_id=${pid}&unit_price=${form.unit_price || 0}&quantity=${form.quantity || 0}`)
        .then(({ data }) => setData(data)).catch(() => setData(null));
    }, 300);
    return () => clearTimeout(t);
  }, [pid, form.unit_price, form.quantity]);

  if (!pid || !data) return null;
  const good = data.margin_per_unit >= 0 && !data.below_floor;

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="sales-margin-preview">
      <div className="flex items-center gap-1.5 mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
        <TrendingUp className="h-3.5 w-3.5 text-[#F47920]" /> Live Margin Preview
      </div>
      {!data.has_cost_data ? (
        <div className="flex items-start gap-1.5 text-xs text-slate-500"><Info className="h-3.5 w-3.5 mt-0.5 shrink-0" /> No landed-cost data yet for this product — add cost components under Landed Cost & Margin for an accurate margin.</div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div><div className="text-[10px] uppercase text-slate-400">Landed / gal</div><div className="font-semibold text-slate-800" data-testid="preview-landed">${fmtNum(data.landed_cost_per_unit, 4)}</div></div>
            <div><div className="text-[10px] uppercase text-slate-400">Your Price / gal</div><div className="font-semibold text-slate-800">${fmtNum(data.unit_price, 4)}</div></div>
            <div><div className="text-[10px] uppercase text-slate-400">Margin / gal</div><div className={`font-semibold ${data.margin_per_unit >= 0 ? "text-emerald-600" : "text-rose-600"}`} data-testid="preview-margin-unit">${fmtNum(data.margin_per_unit, 4)}</div></div>
          </div>
          <div className="mt-2 flex items-center justify-between border-t border-slate-200 pt-2">
            <span className="text-xs text-slate-500">Projected margin{data.quantity ? ` on ${fmtNum(data.quantity)} L` : ""}</span>
            <span className={`text-sm font-bold ${data.margin_total >= 0 ? "text-emerald-600" : "text-rose-600"}`} data-testid="preview-margin-total">{fmtMoney(data.margin_total)} · {fmtNum(data.margin_pct, 1)}%</span>
          </div>
          {data.below_floor && (
            <div className="mt-2 flex items-start gap-1.5 text-xs text-rose-600 font-medium" data-testid="preview-below-floor"><AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" /> Margin {fmtNum(data.margin_pct, 1)}% is below the {data.floor_pct}% floor. Consider raising the price.</div>
          )}
          {!data.below_floor && good && data.unit_price > 0 && (
            <div className="mt-1 text-[11px] text-emerald-600">Above the {data.floor_pct}% margin floor.</div>
          )}
        </>
      )}
    </div>
  );
}
